/**
 * Session Details Modal
 * 
 * Modal for viewing detailed information about an administration session.
 * Shows complete session history, timeline, vitals, and validation results.
 */

import React from 'react';
import {
    Modal,
    Descriptions,
    Timeline,
    Card,
    Row,
    Col,
    Tag,
    Table,
    Alert,
    Space,
    Divider,
    Button
} from 'antd';
import {
    UserOutlined,
    CheckCircleOutlined,
    MedicineBoxOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import AdministrationHistoryButton from './AdministrationHistoryButton';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { validateAdministrationWindow } from '../../../utils/administrationValidation';

dayjs.extend(duration);

const SessionDetailsModal = ({ 
    open, 
    session, 
    patient, 
    medication, 
    onClose,
    zIndex = 15000 // Very high z-index to ensure modal appears above everything
}) => {
    if (!session) return null;

    const getStatusColor = (status) => {
        const statusColors = {
            'created': 'default',
            'started': 'processing',
            'in_progress': 'processing',
            'paused': 'warning',
            'completed': 'success',
            'cancelled': 'error',
            'failed': 'error'
        };
        return statusColors[status] || 'default';
    };

    const renderTimeline = () => {
        const timelineItems = [];

        // Session created
        timelineItems.push({
            color: 'blue',
            dot: <InfoCircleOutlined />,
            children: (
                <div>
                    <div><strong>Session Created</strong></div>
                    <div>{dayjs(session.created_at).format('DD/MM/YYYY hh:mm:ss A')}</div>
                    {session.scheduled_time && (
                        <div>Scheduled for: {dayjs(session.scheduled_time).format('hh:mm A')}</div>
                    )}
                </div>
            )
        });

        // Administration started
        if (session.start_time) {
            timelineItems.push({
                color: 'green',
                dot: <UserOutlined />,
                children: (
                    <div>
                        <div><strong>Administration Started</strong></div>
                        <div>{dayjs(session.start_time).format('DD/MM/YYYY hh:mm:ss A')}</div>
                        <div>By: {session.start_nurse_name} ({session.start_nurse_id})</div>
                        {session.start_notes && <div>Notes: {session.start_notes}</div>}
                    </div>
                )
            });
        }

        // Administration completed
        if (session.end_time) {
            timelineItems.push({
                color: 'green',
                dot: <CheckCircleOutlined />,
                children: (
                    <div>
                        <div><strong>Administration Completed</strong></div>
                        <div>{dayjs(session.end_time).format('DD/MM/YYYY hh:mm:ss A')}</div>
                        <div>By: {session.end_nurse_name} ({session.end_nurse_id})</div>
                        {session.end_notes && <div>Notes: {session.end_notes}</div>}
                        {session.dose_administered && <div>Dose: {session.dose_administered}</div>}
                        {session.patient_response && <div>Response: {session.patient_response}</div>}
                    </div>
                )
            });
        }

        // Session cancelled
        if (session.status === 'cancelled' && session.cancelled_at) {
            timelineItems.push({
                color: 'red',
                dot: <ExclamationCircleOutlined />,
                children: (
                    <div>
                        <div><strong>Session Cancelled</strong></div>
                        <div>{dayjs(session.cancelled_at).format('DD/MM/YYYY hh:mm:ss A')}</div>
                        <div>By: {session.cancelled_by}</div>
                        <div>Reason: {session.cancellation_reason}</div>
                    </div>
                )
            });
        }

        return <Timeline items={timelineItems} />;
    };

    const renderVitals = (vitals, title) => {
        if (!vitals || vitals.length === 0) {
            return (
                <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                    No {title.toLowerCase()} recorded
                </div>
            );
        }

        const columns = [
            {
                title: 'Type',
                dataIndex: 'type',
                key: 'type',
                render: (type) => type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
            },
            {
                title: 'Value',
                dataIndex: 'value',
                key: 'value'
            },
            {
                title: 'Unit',
                dataIndex: 'unit',
                key: 'unit'
            },
            {
                title: 'Time',
                dataIndex: 'measured_at',
                key: 'measured_at',
                render: (time) => time ? dayjs(time).format('hh:mm:ss A') : '-'
            },
            {
                title: 'Measured By',
                dataIndex: 'measured_by',
                key: 'measured_by'
            },
            {
                title: 'Notes',
                dataIndex: 'notes',
                key: 'notes',
                render: (notes) => notes || '-'
            }
        ];

        return (
            <Table
                columns={columns}
                dataSource={vitals}
                size="small"
                pagination={false}
                rowKey={(record, index) => index}
            />
        );
    };

    const renderWindowValidation = () => {
        if (!session.scheduled_time) return null;

        const validation = session.start_time ? 
            validateAdministrationWindow(session.scheduled_time, session.start_time) :
            validateAdministrationWindow(session.scheduled_time);

        if (!validation) return null;

        return (
            <Alert
                message="Administration Window Validation"
                description={validation.message}
                type={validation.isValid ? 'success' : 'error'}
                showIcon
                style={{ marginBottom: 16 }}
            />
        );
    };

    const getDurationInfo = () => {
        if (!session.start_time) return null;

        if (session.end_time) {
            return {
                label: 'Total Duration',
                value: `${session.duration_minutes} minutes`,
                color: 'success'
            };
        }

        const elapsed = dayjs().diff(dayjs(session.start_time), 'minutes');
        return {
            label: 'Elapsed Time',
            value: `${elapsed} minutes (ongoing)`,
            color: 'processing'
        };
    };

    const durationInfo = getDurationInfo();

    return (
        <>
            {/* Force high z-index for administration modals */}
            <style>
                {`
                .administration-modal-wrapper .ant-modal-wrap {
                    z-index: 15000 !important;
                    position: fixed !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                }
                .administration-modal-wrapper .ant-modal-mask {
                    z-index: 14999 !important;
                    position: fixed !important;
                    background: rgba(0, 0, 0, 0.7) !important;
                }
                .administration-modal-wrapper .ant-modal {
                    z-index: 15000 !important;
                    position: relative !important;
                    margin: 0 auto !important;
                    top: auto !important;
                    left: auto !important;
                    transform: none !important;
                }
                .administration-modal-wrapper {
                    z-index: 15000 !important;
                    position: fixed !important;
                }
                .administration-modal-wrapper .ant-modal-content {
                    pointer-events: auto !important;
                }
                .administration-modal-wrapper .ant-input,
                .administration-modal-wrapper .ant-select,
                .administration-modal-wrapper .ant-form-item {
                    pointer-events: auto !important;
                }
                `}
            </style>
            <Modal
            title={
                <Space>
                    <MedicineBoxOutlined />
                    Administration Session Details
                    <Tag color={getStatusColor(session.status)}>
                        {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                    </Tag>
                </Space>
            }
            open={open}
            onCancel={onClose}
            zIndex={zIndex}
            mask={true}
            maskClosable={false}
            getContainer={() => document.body}
            style={{ zIndex: zIndex }}
            maskStyle={{ zIndex: 14999 }}
            wrapClassName="administration-modal-wrapper"
            destroyOnClose={false}
            forceRender={true}
            focusTriggerAfterClose={false}
            width={1000}
            footer={[
                <AdministrationHistoryButton
                    key="history"
                    patientId={session?.patient_id}
                    patientName={patient?.full_name || patient?.name || 'Patient'}
                    size="default"
                    type="default"
                />,
                <Button key="close" type="primary" onClick={onClose}>
                    Close
                </Button>
            ]}
        >
            {renderWindowValidation()}

            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card title="Session Information" size="small">
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Session ID">
                                {session.session_id}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={getStatusColor(session.status)}>
                                    {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Patient">
                                {patient?.full_name} (MRN: {patient?.mrn})
                            </Descriptions.Item>
                            <Descriptions.Item label="Medication">
                                {session.medication_name}
                            </Descriptions.Item>
                            {session.scheduled_time && (
                                <Descriptions.Item label="Scheduled Time">
                                    {dayjs(session.scheduled_time).format('DD/MM/YYYY hh:mm A')}
                                </Descriptions.Item>
                            )}
                            {durationInfo && (
                                <Descriptions.Item label={durationInfo.label}>
                                    <Tag color={durationInfo.color}>{durationInfo.value}</Tag>
                                </Descriptions.Item>
                            )}
                            {session.handoff_occurred && (
                                <Descriptions.Item label="Handoff">
                                    <Tag color="orange">Different nurses for start/end</Tag>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card title="Timeline" size="small" style={{ height: '100%' }}>
                        {renderTimeline()}
                    </Card>
                </Col>
            </Row>

            {(session.start_vitals?.length > 0 || session.end_vitals?.length > 0) && (
                <>
                    <Divider />
                    <Row gutter={[16, 16]}>
                        {session.start_vitals?.length > 0 && (
                            <Col xs={24} lg={12}>
                                <Card title="Pre-Administration Vitals" size="small">
                                    {renderVitals(session.start_vitals, 'Pre-Administration Vitals')}
                                </Card>
                            </Col>
                        )}

                        {session.end_vitals?.length > 0 && (
                            <Col xs={24} lg={12}>
                                <Card title="Post-Administration Vitals" size="small">
                                    {renderVitals(session.end_vitals, 'Post-Administration Vitals')}
                                </Card>
                            </Col>
                        )}
                    </Row>
                </>
            )}

            {session.side_effects_observed?.length > 0 && (
                <>
                    <Divider />
                    <Card title="Observed Side Effects" size="small">
                        <div>
                            {session.side_effects_observed.map((effect, index) => (
                                <Tag key={index} color="orange" style={{ marginBottom: 4 }}>
                                    {effect}
                                </Tag>
                            ))}
                        </div>
                    </Card>
                </>
            )}
        </Modal>
        </>
    );
};

export default SessionDetailsModal;